import { useRef } from 'react';
import { motion, useScroll } from 'framer-motion';
import { StarIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { Rail } from '../shared/Rail';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages } from '../../contexts/SiteContext';
import { useLayerY } from '../../hooks/useParallax';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';

/**
 * Reviews, na Opbouwen en vóór "één product": de score als kop, daarna alle stemmen als kaarten op de rail.
 * Geen automatisch doordraaien: je leest in je eigen tempo. Het grote aanhalingsteken ligt als
 * achterlaag achter de kaarten en beweegt trager mee. Zonder echte reviews de kop uit Paginateksten.
 */
export function HomeReviews() {
  const pages = usePages();
  const {
    shopRating
  } = useCatalog();
  const copy = pages.home.reviews;
  const quotes = pages.testimonials;
  const count = shopRating.count;
  const avg = rating(shopRating.average);
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const glyphY = useLayerY(scrollYProgress, [-24, 48]);
  return <section ref={ref} className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="home-reviews" data-id="element-7472" data-mp="2gz17p4ix25lm">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7473" data-mp="2lwj8g7ms5eij">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7474" data-mp="278ewrnm5a399">
          <div className="lg:col-span-7" data-id="element-7475" data-mp="2bzy2nk2xrzdn">
            {count > 0 ? <>
                {/* De sterren zijn beeld; de kop eronder zegt hetzelfde in woorden. */}
                <p className="flex items-center gap-1" aria-hidden="true" data-id="element-7476" data-mp="237w1fjl1mf2b">
                  {Array.from({
                length: 5
              }, (_, i) => <StarIcon key={i} className="h-4 w-4 fill-current text-clay" strokeWidth={0} data-id="element-7477" data-mp="1oac1ws6x1c3j" />)}
                </p>
                <h2 id="home-reviews" className="mt-4 type-sub text-ink" data-id="element-7478" data-mp="1t8x1cpecfrqx">
                  {avg} uit {plural(count, 'beoordeling', 'beoordelingen')}
                  <span className="sr-only" data-id="element-7479" data-mp="15dtoc8ar4st5"> (van 5 sterren)</span>
                </h2>
              </> : <h2 id="home-reviews" className="type-sub text-ink" data-id="element-7480" data-mp="10gbm10r8pbu4">
                <HeadingText heading={copy.heading} data-id="element-7481" data-mp="vgfhn0emrie1" />
              </h2>}
          </div>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-7482" data-mp="1t47j46kq15rf">{fill(copy.body)}</p>}
        </div>

        {quotes.length > 0 && <div className="relative mt-12 lg:mt-16" data-id="element-7483" data-mp="1o30ay6eo24k8">
            <motion.span aria-hidden="true" style={glyphY ? {
          y: glyphY
        } : undefined} className="ornament-quote pointer-events-none absolute -top-14 left-0 text-clay sm:-top-20" data-id="element-7484" data-mp="1j4qgjy0rn7zo">
              “
            </motion.span>
            <Rail id="home-reviews-rail" label="Reviews" className="relative" items={quotes} itemKey={t => `${t.name}-${t.context}`} itemClassName="w-[86%] sm:w-[62%] lg:w-[calc((100%-3rem)/2.5)]" renderItem={t => <figure className="flex w-full flex-col bg-sand p-6 sm:p-8" data-id="element-7486" data-mp="2oc00ee8d7f81">
                  <blockquote className="type-title leading-snug text-ink" data-id="element-7487" data-mp="2t49orptu0787">{fill(t.quote)}</blockquote>
                  <figcaption className="mt-auto pt-6 text-[14px] text-muted" data-id="element-7488" data-mp="2y3dychko46z2">
                    {t.name} · {t.context}
                  </figcaption>
                </figure>} data-id="element-7485" data-mp="2r0awmu00bnx" />
          </div>}
      </div>
    </section>;
}